<section class="sync-rules" aria-labelledby="company-sync-rules-title">
  <div>
    <h4 id="company-sync-rules-title">Which companies should sync automatically?</h4>
    <p>Use existing HubSpot company fields to choose who qualifies. Your sync switches and frequency still apply.</p>
  </div>
  <mat-form-field appearance="outline">
    <mat-label>Companies included</mat-label>
    <mat-select [value]="policy.mode" (selectionChange)="setMode($event.value)" [disabled]="disabled">
      <mat-option value="all">All companies</mat-option>
      <mat-option value="matching">Only companies matching these rules</mat-option>
    </mat-select>
  </mat-form-field>
  @if (policy.mode === 'matching') {
    <p>A company must match every condition in at least one group. Missing values wait for more information, unless you explicitly select “is empty”.</p>
    @for (group of policy.groups; track $index; let groupIndex = $index) {
      @if (groupIndex > 0) {<strong class="group-or">OR</strong>}
      <div class="rule-group">
        <div class="group-heading"><strong>Group {{ groupIndex + 1 }} — match all conditions</strong>
          <button mat-button type="button" (click)="removeGroup(groupIndex)" [disabled]="disabled" [attr.aria-label]="'Remove group ' + (groupIndex + 1)">Remove group</button>
        </div>
        @for (condition of group.conditions; track $index; let conditionIndex = $index) {
          <div class="rule-row">
            <mat-form-field appearance="outline"><mat-label>Company field</mat-label>
              <mat-select [value]="condition.propertyName" (selectionChange)="setProperty(condition, $event.value)" [disabled]="disabled" [attr.aria-label]="'Group ' + (groupIndex + 1) + ' condition ' + (conditionIndex + 1) + ' field'">
                @for (property of availableProperties; track property.name) {<mat-option [value]="property.name">{{ property.label }} ({{ property.name }})</mat-option>}
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline"><mat-label>Condition</mat-label>
              <mat-select [value]="condition.operator" (selectionChange)="setOperator(condition, $event.value)" [disabled]="disabled" [attr.aria-label]="'Group ' + (groupIndex + 1) + ' condition ' + (conditionIndex + 1) + ' operator'">
                @for (operator of operators(condition); track operator.value) {<mat-option [value]="operator.value">{{ operator.label }}</mat-option>}
              </mat-select>
            </mat-form-field>
            @if (condition.operator !== 'known' && condition.operator !== 'unknown') {
              @if (condition.propertyType === 'enumeration' || condition.propertyType === 'bool') {
                <mat-form-field appearance="outline"><mat-label>Values</mat-label>
                  @if (condition.operator === 'in' || condition.operator === 'not_in') {
                    <mat-select multiple [value]="condition.values" (selectionChange)="setValues(condition, $event.value)" [disabled]="disabled" aria-label="Rule values">
                      @for (option of options(condition); track option.value) {<mat-option [value]="option.value">{{ option.label }}</mat-option>}
                    </mat-select>
                  } @else {
                    <mat-select [value]="condition.values[0]" (selectionChange)="setValues(condition, $event.value)" [disabled]="disabled" aria-label="Rule value">
                      @for (option of options(condition); track option.value) {<mat-option [value]="option.value">{{ option.label }}</mat-option>}
                    </mat-select>
                  }
                </mat-form-field>
              } @else {
                <mat-form-field appearance="outline"><mat-label>Value</mat-label>
                  <textarea matInput rows="1" [value]="condition.values.join('\n')" (input)="inputValue(condition, $event)" [disabled]="disabled" aria-label="Rule value"></textarea>
                  @if (condition.operator === 'in' || condition.operator === 'not_in') {<mat-hint>One value per line, up to 20.</mat-hint>}
                </mat-form-field>
              }
            }
            <button mat-icon-button type="button" (click)="removeCondition(groupIndex, conditionIndex)" [disabled]="disabled" [attr.aria-label]="'Remove condition ' + (conditionIndex + 1)"><mat-icon>delete_outline</mat-icon></button>
          </div>
        }
        <button mat-stroked-button type="button" (click)="addCondition(groupIndex)" [disabled]="disabled || group.conditions.length >= 10">Add AND condition</button>
      </div>
    }
    <button mat-stroked-button type="button" (click)="addGroup()" [disabled]="disabled || policy.groups.length >= 5">Add OR group</button>
    @if (!rulesValid) {<p class="rule-warning" role="status">Complete at least one condition in each group. Up to five groups and ten conditions per group are supported.</p>}
    @if (missingTracking.length) {
      <p class="rule-warning" role="status">Automatic sync cannot yet track changes to
        @for (property of missingTracking; track property.name; let last = $last) {{{ property.label }}{{ last ? '.' : ', ' }}}
        You can preview these rules, but saving requires change tracking for those fields.</p>
    }
  }
  <div class="preview-actions"><button mat-stroked-button type="button" (click)="runPreview()" [disabled]="disabled || !rulesValid || previewBusy || !clientId">{{ previewBusy ? 'Loading preview…' : 'Preview matching companies' }}</button>
    <span>Read-only. Previewing never starts a sync.</span>
  </div>
  @if (previewError) {<p class="rule-warning" role="alert">{{ previewError }}</p>}
  @if (preview) {
    <div class="preview-summary" aria-live="polite"><strong>This page: {{ preview.pageSize }} companies</strong>
      <span>{{ preview.counts.eligible }} matching</span><span>{{ preview.counts.excluded }} excluded</span><span>{{ preview.counts.pending }} need information</span>
    </div>
    <p>Results cover this page only, not the whole portal. Unsaved rules are used for this preview.</p>
    <div class="preview-items">
      @for (company of preview.items; track company.id) {<div class="preview-item"><span>{{ company.name || 'Company ' + company.id }}</span><span>{{ company.decision === 'eligible' ? 'Matching' : company.decision === 'pending' ? 'Needs information' : 'Excluded' }}</span></div>}
    </div>
    @if (preview.nextCursor) {<button mat-button type="button" (click)="runPreview(true)" [disabled]="previewBusy">Preview next page</button>}
  }
  <p>Companies that stop qualifying stay in HubSpot. D&amp;B refreshes continue to follow your saved sync schedule.</p>
</section>
